import {ReadOnlyFieldset} from '@/components';
import {TrustDetailsStep} from '@/features/trust-accounts/activation/config';
import {useTrustDetailsPackageQuery} from '@/features/trust-accounts/activation/hooks';

import {AssetDetailsStepForm, SellerDetailsStepForm, TrustScopeStepForm} from './components';

import type {FormStateSnapshot, StepStatus} from '@/hooks';
import type {FC} from 'react';

type TrustAccountActivationDetailsFormProps = {
  trustAccountId: string;
  activeStep: TrustDetailsStep;
  readOnly?: boolean;
  documentsReadOnly?: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
  onStepStatus?: (step: string, status: StepStatus) => void;
};

export const TrustAccountActivationDetailsForm: FC<TrustAccountActivationDetailsFormProps> = ({
  trustAccountId,
  activeStep,
  readOnly = false,
  documentsReadOnly,
  onFormStateChange,
  onStepStatus
}) => {
  const {data, isLoading, isError} = useTrustDetailsPackageQuery(trustAccountId);
  const scope = data?.data.scope;
  const asset = data?.data.asset;
  const sellers = data?.data.sellers;

  const makeStepHandler = (step: TrustDetailsStep) => (state: FormStateSnapshot) => {
    onStepStatus?.(step, state);
    if (activeStep === step) onFormStateChange?.(state);
  };

  return (
    <div>
      <ReadOnlyFieldset readOnly={readOnly}>
        <div hidden={activeStep !== TrustDetailsStep.TRUST_SCOPE}>
          <TrustScopeStepForm
            trustAccountId={trustAccountId}
            scope={scope}
            isLoading={isLoading}
            isError={isError}
            onFormStateChange={makeStepHandler(TrustDetailsStep.TRUST_SCOPE)}
          />
        </div>
      </ReadOnlyFieldset>

      <div hidden={activeStep !== TrustDetailsStep.ASSET_DETAILS}>
        <AssetDetailsStepForm
          trustAccountId={trustAccountId}
          asset={asset}
          isLoading={isLoading}
          isError={isError}
          readOnly={readOnly}
          documentsReadOnly={documentsReadOnly}
          onFormStateChange={makeStepHandler(TrustDetailsStep.ASSET_DETAILS)}
        />
      </div>

      <div hidden={activeStep !== TrustDetailsStep.SELLER_DETAILS}>
        <SellerDetailsStepForm
          trustAccountId={trustAccountId}
          sellers={sellers}
          isLoading={isLoading}
          isError={isError}
          readOnly={readOnly}
          isActive={activeStep === TrustDetailsStep.SELLER_DETAILS}
          documentsReadOnly={documentsReadOnly}
          onFormStateChange={makeStepHandler(TrustDetailsStep.SELLER_DETAILS)}
        />
      </div>
    </div>
  );
};
